import { useCallback, useMemo, useState } from 'react';

import { DobError } from './dob-error.js';
import { DOB_MIN_DATE } from './token.constants.js';
import { todayIso } from './today-iso.js';

/**
 * Optionen für `useDobValidation`. Tests injizieren `today` als ISO-String,
 * damit Datum-Vergleiche unabhängig von der Wall-Clock laufen.
 */
interface UseDobValidationOptions {
  readonly today?: string;
}

/**
 * Rückgabewert: aktueller Eingabewert, Setter, ein optionaler Fehler-
 * Diskriminator und ein abgeleitetes `isValid`. Konsument bindet `value`/
 * `onChange` an `<input type="date">` und liest `error` für die Inline-
 * Microcopy.
 */
interface UseDobValidationResult {
  readonly value: string;
  readonly setValue: (next: string) => void;
  readonly error: DobError | null;
  readonly isValid: boolean;
  readonly minDate: string;
  readonly maxDate: string;
}

/**
 * Custom-Hook für die Geburtsdatum-Eingabe (AFO-008).
 *
 * Regeln (alle bei Submit-Zeit zu prüfen):
 * - Pflichtfeld (`required`).
 * - Maximum = heute (`future` bei Zukunftsdatum).
 * - Minimum = `1900-01-01` (`tooOld` bei Datum davor).
 *
 * Der Hook validiert eager: jeder `setValue`-Aufruf re-evaluiert den Fehler.
 * Die Komponente entscheidet, wann sie den Fehler **anzeigt** (typischerweise
 * nach erstem Submit-Versuch oder Blur). `isValid` ist die kanonische Gate-
 * Bedingung für den Submit-Button.
 */
export function useDobValidation(options: UseDobValidationOptions = {}): UseDobValidationResult {
  const maxDate = options.today ?? todayIso();
  const [value, setValueState] = useState<string>('');
  const error = useMemo(() => validateDob(value, maxDate), [value, maxDate]);
  const setValue = useCallback((next: string) => setValueState(next), []);
  return {
    value,
    setValue,
    error,
    isValid: error === null && value !== '',
    minDate: DOB_MIN_DATE,
    maxDate,
  };
}

/**
 * Pure Validierung. ISO-Strings (`YYYY-MM-DD`) sind lexikografisch sortiert
 * gleichbedeutend mit Datums-Vergleich, daher reicht String-`>`/`<`.
 */
function validateDob(value: string, maxDate: string): DobError | null {
  if (value === '') {
    return DobError.Required;
  }
  if (value > maxDate) {
    return DobError.Future;
  }
  if (value < DOB_MIN_DATE) {
    return DobError.TooOld;
  }
  return null;
}
